/* Careatic festive motion layer.
 *
 * One file, no dependencies, defer-loaded. Everything degrades: if this never
 * runs, posters and stills still render, the breed menu is a plain list, the
 * FAQ shows every answer, and nothing shifts. That matters more than usual
 * here because the store converts on Indian 4G with no ad spend behind it.
 *
 * Rules held throughout:
 *   - transform and opacity only, never layout properties
 *   - no video file loads until its card is on screen; every clip on screen
 *     plays muted, and pauses the moment it leaves (Arun, 18 Sep 2026)
 *   - prefers-reduced-motion removes motion rather than shortening it
 */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var supportsIO = 'IntersectionObserver' in window;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  /* sound: one clip at a time. The flag lives on the video and on its row, so
     the drift can hold still while someone listens. */
  function setSound(v, on) {
    v.muted = !on;
    if (on) v.setAttribute('data-ca-sound', ''); else v.removeAttribute('data-ca-sound');
    var card = v.closest('.ca-vidcard');
    var btn = card && card.querySelector('[data-ca-vidmute]');
    if (btn) btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    var row = v.closest('[data-ca-vidrow]');
    if (row) {
      if (row.querySelector('video[data-ca-sound]')) row.setAttribute('data-ca-sound', '');
      else row.removeAttribute('data-ca-sound');
    }
  }

  /* The speaker button on a clip. Delegated once for the whole document, because
     the kurta and lehenga swap replaces the page and runs this file again. */
  function videoSound() {
    if (window.__caVidSound) return;
    window.__caVidSound = true;
    document.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest ? e.target.closest('[data-ca-vidmute]') : null;
      if (!btn) return;
      e.preventDefault();
      var card = btn.closest('.ca-vidcard');
      var v = card && card.querySelector('video');
      if (!v) return;
      var turnOn = !v.hasAttribute('data-ca-sound');
      [].slice.call(document.querySelectorAll('video[data-ca-sound]')).forEach(function (o) {
        if (o !== v) setSound(o, false);
      });
      if (!turnOn) { setSound(v, false); return; }
      if (!v.src && v.dataset.src) v.src = v.dataset.src;
      setSound(v, true);
      var p = v.play();
      if (p && p.then) {
        p.then(function () { v.classList.add('is-playing'); })
         .catch(function () { setSound(v, false); });
      }
    });
  }

  /* lazy video: every clip on screen plays, muted and looped.
     The file is only requested once its card is on screen, so nothing below the
     fold or round the row's corner costs data. A clip that leaves the screen
     pauses, and keeps its last frame (the class stays) so it does not flash back
     to the poster when the loop brings it round again. */
  function lazyVideos() {
    var vids = [].slice.call(document.querySelectorAll('[data-ca-lazyvideo]'));
    if (!vids.length) return;

    // No IO, or the visitor asked for less motion: leave the posters up.
    if (!supportsIO || reduced) return;

    /* A row can ask for a clip to be mostly on screen before it plays
       (data-ca-play-min on the row, e.g. 0.6). Then a row showing four cards
       never plays more than four at once, even mid slide (Arun, 12 Oct 2026). */
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var v = e.target;
        var minRow = v.closest('[data-ca-play-min]');
        var need = minRow ? (parseFloat(minRow.getAttribute('data-ca-play-min')) || 0.15) : 0.15;
        if (e.isIntersecting && e.intersectionRatio >= need - 0.01) {
          if (!v.src && v.dataset.src) v.src = v.dataset.src;
          if (!v.hasAttribute('data-ca-sound')) v.muted = true;
          var p = v.play();
          if (p && p.then) {
            p.then(function () { v.classList.add('is-playing'); })
             .catch(function () { /* autoplay refused, poster stays */ });
          } else {
            v.classList.add('is-playing');
          }
        } else {
          // Leaving the screen ends its sound too, so the row drifts on.
          if (v.hasAttribute('data-ca-sound')) setSound(v, false);
          if (!v.paused) v.pause();
        }
      });
    }, { threshold: [0.15, 0.4, 0.6, 0.8] });

    vids.forEach(function (v) { io.observe(v); });
  }

  /* breed switcher */
  function breedSwitch() {
    var roots = [].slice.call(document.querySelectorAll('[data-ca-breedsw]'));
    roots.forEach(function (root) {
      var trigger = root.querySelector('[data-ca-breedsw-trigger]');
      var menu = root.querySelector('[data-ca-breedsw-menu]');
      var range = root.querySelector('[data-ca-breedsw-range]');
      if (!trigger || !menu) return;

      // The current breed's range lives on the active row, so the trigger
      // never needs its own copy of the ladder.
      var current = menu.querySelector('.is-current .ca-breedsw__rowrange');
      if (range && current) range.textContent = current.textContent;

      function close() {
        menu.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
      }
      function open() {
        menu.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
        var cur = menu.querySelector('.is-current');
        if (cur) cur.scrollIntoView({ block: 'nearest' });
      }

      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        if (menu.hidden) open(); else close();
      });

      document.addEventListener('click', function (e) {
        if (!root.contains(e.target)) close();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !menu.hidden) { close(); trigger.focus(); }
      });
    });
  }

  /* FAQ accordion */
  function faq() {
    var groups = [].slice.call(document.querySelectorAll('[data-ca-faq]'));
    groups.forEach(function (group) {
      var items = [].slice.call(group.querySelectorAll('[data-ca-faq-item]'));
      items.forEach(function (item, i) {
        var btn = item.querySelector('[data-ca-faq-q]');
        var panel = item.querySelector('[data-ca-faq-a]');
        if (!btn || !panel) return;

        function setState(on) {
          panel.hidden = !on;
          btn.setAttribute('aria-expanded', on ? 'true' : 'false');
        }

        // Progressive enhancement: markup ships open, JS closes all but the first.
        setState(i === 0);

        btn.addEventListener('click', function () {
          var next = panel.hidden;
          items.forEach(function (other) {
            var op = other.querySelector('[data-ca-faq-a]');
            var ob = other.querySelector('[data-ca-faq-q]');
            if (op && ob) { op.hidden = true; ob.setAttribute('aria-expanded', 'false'); }
          });
          if (next) { panel.hidden = false; btn.setAttribute('aria-expanded', 'true'); }
        });
      });
    });
  }

  /* section reveals, fires once */
  function reveals() {
    if (reduced || !supportsIO) return;
    var els = [].slice.call(document.querySelectorAll('[data-ca-reveal]'));
    if (!els.length) return;

    els.forEach(function (el) { el.classList.add('ca-reveal'); });

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var el = e.target;
        var kids = [].slice.call(el.querySelectorAll('[data-ca-reveal-item]'));
        if (kids.length) {
          kids.forEach(function (k, i) {
            k.style.transitionDelay = (i * 150) + 'ms';
            k.classList.add('is-in');
          });
        }
        el.classList.add('is-in');
        io.unobserve(el);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.05 });

    els.forEach(function (el) { io.observe(el); });
  }

  /* video row: arrows, plus a continuous drift right to left, in a loop.
     data-ca-vidrow-auto on the section carries the milliseconds one card takes
     to drift past; no attribute, no drift. The track keeps its own swipe. It
     glides (no one card jumps, Arun 18 Sep 2026), so snapping is switched off
     while it runs. To loop without rewinding, a card that has fully left on
     the left is moved to the end of the row and scrollLeft is pulled back one
     card in the same frame, so nothing on screen moves. It starts the moment
     the page is ready, on screen or not, so the row is already moving when the
     shopper reaches it (Arun). It waits while a mouse is over it, while it has
     keyboard focus and for a few seconds after any swipe, wheel or arrow tap;
     a hidden tab stops it for free (no animation frames). Still in the theme
     editor, and never runs for reduced motion. */
  function videoRow() {
    var rows = [].slice.call(document.querySelectorAll('[data-ca-vidrow]'));
    var designMode = !!(window.Shopify && window.Shopify.designMode);
    rows.forEach(function (row) {
      var track = row.querySelector('[data-ca-vidrow-track]');
      var prev = row.querySelector('[data-ca-vidrow-prev]');
      var next = row.querySelector('[data-ca-vidrow-next]');
      if (!track) return;

      var autoMs = parseInt(row.getAttribute('data-ca-vidrow-auto') || '0', 10);
      var loop = autoMs > 0;

      function cardW() {
        var card = track.querySelector('.ca-vidcard');
        var gap = parseFloat(window.getComputedStyle(track).columnGap) || 20;
        return card ? card.getBoundingClientRect().width + gap : 289;
      }
      function overflows() { return track.scrollWidth > track.clientWidth + 2; }

      /* Carry one card across the seam without a visible jump. */
      function rotate(dir) {
        var cards = track.querySelectorAll('.ca-vidcard');
        if (cards.length < 2) return;
        var w = cardW();
        // Put back whatever snap the row had: none while it drifts.
        var snapWas = track.style.scrollSnapType;
        track.style.scrollSnapType = 'none';
        if (dir > 0) {
          track.appendChild(cards[0]);
          track.scrollLeft -= w;
        } else {
          track.insertBefore(cards[cards.length - 1], cards[0]);
          track.scrollLeft += w;
        }
        track.style.scrollSnapType = snapWas;
      }

      function step(dir) {
        var w = cardW();
        if (loop && overflows()) {
          if (dir > 0 && track.scrollLeft >= w - 2) rotate(1);
          if (dir < 0 && track.scrollLeft < w - 2) rotate(-1);
        }
        track.scrollBy({ left: dir * w, behavior: reduced ? 'auto' : 'smooth' });
      }

      var holdUntil = 0;
      function hold(ms) { holdUntil = Date.now() + (ms || 6000); }

      if (prev) prev.addEventListener('click', function () { hold(); step(-1); });
      if (next) next.addEventListener('click', function () { hold(); step(1); });

      function sync() {
        /* A looping row has no ends, so both arrows stay live. */
        if (loop && overflows()) {
          if (prev) prev.classList.add('is-on');
          if (next) next.classList.add('is-on');
          return;
        }
        var atStart = track.scrollLeft <= 2;
        var atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
        if (prev) prev.classList.toggle('is-on', !atStart);
        if (next) next.classList.toggle('is-on', !atEnd);
      }
      track.addEventListener('scroll', sync, { passive: true });
      sync();

      if (!loop || reduced || designMode) return;

      /* data-ca-vidrow-mode="step": instead of the drift, the row holds whole
         cards on screen and slides one card along every autoMs, looping
         (homepage, Arun 12 Oct 2026: "only 4 video playing at a time"). */
      var stepMode = row.getAttribute('data-ca-vidrow-mode') === 'step';

      /* Five clips only just overflow a wide screen, so the row can scroll less
         than one card and there is nothing to carry across the seam. One copy of
         the set gives the loop room. The copies are hidden from screen readers
         and the tab order, and this runs before lazyVideos() so their clips load
         on sight like the originals. */
      if (!stepMode && overflows() && track.scrollWidth - track.clientWidth < 2 * cardW()) {
        [].slice.call(track.querySelectorAll('.ca-vidcard')).forEach(function (c) {
          var k = c.cloneNode(true);
          k.setAttribute('aria-hidden', 'true');
          [].slice.call(k.querySelectorAll('a, button')).forEach(function (el) { el.setAttribute('tabindex', '-1'); });
          k.removeAttribute('data-shopify-editor-block');
          k.setAttribute('data-ca-vidrow-copy', '');
          var v = k.querySelector('video');
          if (v) { v.removeAttribute('src'); v.classList.remove('is-playing'); }
          track.appendChild(k);
        });
      }

      /* Hover counts for a real mouse only; a tap on a phone fires a synthetic
         enter and no leave, which would park the row for good. Focus counts only
         when it came from the keyboard, so clicking an arrow does not stop it. */
      var hover = false, focused = false;
      row.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') hover = true; });
      row.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') hover = false; });
      row.addEventListener('focusin', function (e) {
        try { focused = e.target.matches(':focus-visible'); } catch (err) { focused = false; }
      });
      row.addEventListener('focusout', function () { focused = false; });
      ['pointerdown', 'touchstart', 'wheel'].forEach(function (ev) {
        track.addEventListener(ev, function () { hold(8000); }, { passive: true });
      });

      if (stepMode) {
        var timer = window.setInterval(function () {
          if (!document.body.contains(row)) { window.clearInterval(timer); return; }
          if (document.hidden || !overflows()) return;
          if (hover || focused || Date.now() < holdUntil || row.hasAttribute('data-ca-sound')) return;
          step(1);
        }, autoMs);
        return;
      }

      /* Continuous drift. pos is kept as a float so the speed stays even on
         screens that round scrollLeft to whole pixels. */
      track.style.scrollSnapType = 'none';
      var cw = cardW(), room = overflows();
      window.addEventListener('resize', function () { cw = cardW(); room = overflows(); }, { passive: true });
      var pxPerMs = cw / autoMs;
      var pos = track.scrollLeft, last = 0;

      function frame(t) {
        /* The kurta and lehenga swap replaces the page and runs this file again.
           A loop left on the old, detached row stops itself here. */
        if (!document.body.contains(row)) return;
        var dt = last ? Math.min(t - last, 64) : 0;
        last = t;
        // A clip with its sound on holds the row still until it is switched off.
        var moving = room && !hover && !focused && Date.now() >= holdUntil && !row.hasAttribute('data-ca-sound');
        if (moving) {
          // A swipe or an arrow moved the row: carry on from where it is now.
          if (Math.abs(track.scrollLeft - pos) > 3) pos = track.scrollLeft;
          pos += pxPerMs * dt;
          if (pos >= cw) {
            var first = track.querySelector('.ca-vidcard');
            if (first) { track.appendChild(first); pos -= cw; }
          }
          track.scrollLeft = pos;
        } else {
          pos = track.scrollLeft;
        }
        window.requestAnimationFrame(frame);
      }
      window.requestAnimationFrame(frame);
    });
  }

  /* size finder: one breed panel at a time */
  function sizeFinder() {
    var roots = [].slice.call(document.querySelectorAll('[data-ca-sizefinder]'));
    roots.forEach(function (root) {
      var panels = [].slice.call(root.querySelectorAll('[data-ca-sf-panel]'));
      var picks = [].slice.call(root.querySelectorAll('[data-ca-sf-pick]'));
      var nameEl = root.querySelector('[data-ca-sf-name]');
      var subEl = root.querySelector('[data-ca-sf-sub]');
      var menu = root.querySelector('[data-ca-breedsw-menu]');
      var trigger = root.querySelector('[data-ca-breedsw-trigger]');
      if (!panels.length || !picks.length) return;

      // Only now does the page collapse to one panel. Without this script every
      // breed stays visible, which is a usable chart rather than a broken control.
      root.classList.add('ca-sf--live');

      function show(slug) {
        panels.forEach(function (p) {
          p.classList.toggle('is-shown', p.getAttribute('data-ca-sf-panel') === slug);
        });
        picks.forEach(function (b) {
          b.classList.toggle('is-current', b.getAttribute('data-ca-sf-pick') === slug);
        });
      }

      picks.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var slug = btn.getAttribute('data-ca-sf-pick');
          if (nameEl) nameEl.textContent = btn.getAttribute('data-name') || slug;
          if (subEl) subEl.textContent = btn.getAttribute('data-sub') || '';
          show(slug);
          if (menu) menu.hidden = true;
          if (trigger) trigger.setAttribute('aria-expanded', 'false');
          try { history.replaceState(null, '', '?breed=' + slug); } catch (e) {}
        });
      });

      // Deep link support, so an ad can land straight on a breed.
      var m = /[?&]breed=([a-z-]+)/.exec(window.location.search);
      if (m) {
        var btn = root.querySelector('[data-ca-sf-pick="' + m[1] + '"]');
        if (btn) { btn.click(); return; }
      }
    });
  }

  ready(function () {
    videoRow();
    lazyVideos();
    videoSound();
    breedSwitch();
    faq();
    reveals();
    sizeFinder();
  });
}());
